import { HandoffTypeMetric } from '../../handoff-metrics/handoff.constants';
import { Result } from '../../handoff-metrics/handoff-metrics.types';
import {
  BaseCriteriaResult,
  ChecklistMetricStatus,
} from '../../handoff-checklist-v2/handoff-checklist-v2.types';
import { UnifyCriteriaDefinition, UnifyMetricEvalContext } from '../unify-metrics.types';
import { fetchMultipleDependencyData } from '../queries/dependency-fetchers';

type DependentMetricData = Parameters<HandoffTypeMetric['resultFn']>[0];

export interface AdaptHandoffMetricOptions {
  description?: string;
  whereToFix?: {
    label: string;
    url: string | ((ctx: UnifyMetricEvalContext) => string);
  };
  shouldSkip?: (ctx: UnifyMetricEvalContext) => boolean;
}

export const adaptHandoffMetric = (
  metric: HandoffTypeMetric,
  options?: AdaptHandoffMetricOptions
): UnifyCriteriaDefinition => {
  const hasExceptions = Array.isArray(metric.exceptions) && metric.exceptions.length > 0;

  return {
    description: options?.description || metric.testCriteriaFn(),
    whereToFix: options?.whereToFix,
    exceptionOptions: metric.exceptions,
    exceptionType: hasExceptions ? 'dropdown' : 'none',
    shouldSkip: (ctx: UnifyMetricEvalContext) => {
      if (options?.shouldSkip && options.shouldSkip(ctx)) {
        return true;
      }
      if (metric.shouldSkip) {
        return metric.shouldSkip({
          officeConfigurations: ctx.officeConfigurations,
          customizationFeatures: ctx.features,
        });
      }
      return false;
    },
    evaluate: async (ctx: UnifyMetricEvalContext): Promise<BaseCriteriaResult> => {
      const keys = metric.dependencyDataKeys || [];
      const dependentData = await fetchMultipleDependencyData(ctx, keys);
      const fullDependentData = dependentData as DependentMetricData;

      try {
        const actualValue = metric.actualValueFn(fullDependentData);
        const result = metric.resultFn(fullDependentData);

        const isPassed = result === Result.Pass;
        return {
          status: isPassed ? ChecklistMetricStatus.PASSED : ChecklistMetricStatus.BLOCKED,
          actualValue,
          expectedValue: metric.expectedValue,
          message: isPassed ? 'Passed' : undefined,
        };
      } catch (err: unknown) {
        return {
          status: ChecklistMetricStatus.BLOCKED,
          actualValue: undefined,
          expectedValue: metric.expectedValue,
          message: err instanceof Error ? err.message : String(err),
        };
      }
    },
  };
};
